import * as angular from 'angular';
import * as _ from 'lodash';

import { IWidget, WidgetBase } from '../models';
import { WideError } from '../../common';
import { ErrorLevel, DashboardPeriod } from '../../models/enum';
import { Dashboard } from '../../models';
import { DashboardThumbnailWidgetDefinition as definition } from './dashboardThumbnail.definition';

export class DashboardThumbnailWidget extends WidgetBase {
    backgroundUrl?: string;
    categories: string[];
    col = 0;
    dashboard: Dashboard;
    defaultPeriod: DashboardPeriod;
    groupTypes: string[];
    isAutoRefresh: boolean;
    isHomePage: boolean;
    name = definition.title;
    referenceGroup?: string;
    row = 0;
    shortComment?: string;
    sizeX = 6;
    sizeY = 6;
    sortOrder: number;
    type = definition.type;

    private _extraErrors: WideError[];
    private _errorList: WideError[];

    constructor() {
        super();

        this._extraErrors = [];
        this._errorList = [];
    }

    copy(): DashboardThumbnailWidget {
        return {
            name: this.name,
            defaultPeriod: this.defaultPeriod,
            groupTypes: this.groupTypes,
            referenceGroup: this.referenceGroup,
            isHomePage: this.isHomePage,
            sortOrder: this.sortOrder,
            categories: this.categories,
            isAutoRefresh: this.isAutoRefresh,
            dashboard: {
                name: this.name,
                defaultPeriod: this.defaultPeriod,
                groupTypes: this.groupTypes,
                referenceGroup: this.referenceGroup,
                isHomePage: this.isHomePage,
                sortOrder: this.sortOrder,
                categories: this.categories,
                isAutoRefresh: this.isAutoRefresh
            } as Dashboard
        } as DashboardThumbnailWidget;
    }

    restore(src: DashboardThumbnailWidget) {
        this.name = src.name;
        this.defaultPeriod = src.defaultPeriod;
        this.groupTypes = src.groupTypes;
        this.referenceGroup = src.referenceGroup;
        this.isHomePage = src.isHomePage;
        this.sortOrder = src.sortOrder;
        this.categories = src.categories;
        this.isAutoRefresh = src.isAutoRefresh;
        const { dashboard } = this;
        this.dashboard = {} as Dashboard;
        this.dashboard.name = dashboard.name;
        this.dashboard.defaultPeriod = dashboard.defaultPeriod;
        this.dashboard.groupTypes = dashboard.groupTypes;
        this.dashboard.referenceGroup = dashboard.referenceGroup;
        this.dashboard.isHomePage = dashboard.isHomePage;
        this.dashboard.sortOrder = dashboard.sortOrder;
        this.dashboard.categories = src.dashboard.categories;
        this.dashboard.isAutoRefresh = dashboard.isAutoRefresh;
    }

    clearErrors(): void {
        this._extraErrors = [];
        this._errorList = [];
    }

    addError(error: WideError): void {
        this._extraErrors.push(error);
    }

    getError(): WideError {
        this._errorList = ([] as WideError[]).concat(this._extraErrors);
        this.configuration.widgets
            .forEach((w: IWidget) => {
                if (w && w.getError) {
                    const wideError = w.getError();
                    if (wideError) {
                        this._errorList.push(wideError);
                    }
                }
            });

        // add each error message to its collection
        const filterMessages = (level: ErrorLevel) => <string[]>_.chain(this._errorList)
            .filter((error: WideError) => error !== null && error.level === level)
            .map('message')
            .value();

        const fatalErrorMessages = filterMessages(ErrorLevel.Fatal);
        const errorErrorMessages = filterMessages(ErrorLevel.Error);
        const warningErrorMessages = filterMessages(ErrorLevel.Warning);
        const infoErrorMessages = filterMessages(ErrorLevel.Info);

        // PREPARE HEADER MESSAGE
        const headerMessage = this.getHeaderMessage(fatalErrorMessages.length, errorErrorMessages.length, warningErrorMessages.length, infoErrorMessages.length);
        const errorMessages = fatalErrorMessages.concat(errorErrorMessages).concat(warningErrorMessages).concat(infoErrorMessages);
        const errorsCount = fatalErrorMessages.length + errorErrorMessages.length + warningErrorMessages.length + infoErrorMessages.length;

        // GET CONCATENED MESSAGE
        const concatenedMessage = this.getConcatenedMessage(errorsCount, headerMessage, errorMessages);

        // get worst error
        const worstError: WideError = this.getWorstError(this._errorList, concatenedMessage);

        return worstError;
    }

    private getHeaderMessage(fatalErrorMessagesLength: number, errorErrorMessagesLength: number,
        warningErrorMessagesLength: number, infoErrorMessagesLength: number): string {

        // PREPARE HEADER MESSAGE
        let headerMessage = '';

        // function for concat header
        const concatHeader = (header: string, errorCount: number, errorStr: string) => {
            if (errorCount > 0) {
                header += errorCount + ' ' + errorStr;
                if (errorCount > 1) {
                    header += 's';
                }
                header += ', ';
            }
            return header;
        };

        // concat
        headerMessage = concatHeader(headerMessage, fatalErrorMessagesLength, 'fatal');
        headerMessage = concatHeader(headerMessage, errorErrorMessagesLength, 'error');
        headerMessage = concatHeader(headerMessage, warningErrorMessagesLength, 'warning');
        headerMessage = concatHeader(headerMessage, infoErrorMessagesLength, 'info');

        // remove extra ', ' at the end of line
        if (headerMessage.length > 0) {
            headerMessage = headerMessage.substring(0, headerMessage.length - 2);
        }

        return headerMessage;
    }

    private getConcatenedMessage(errorCount: number, headerMessage: string, errorMessages: string[]): string {

        // GET CONCATENED MESSAGE
        let concatenedMessage = '';
        let bodyMessage = '';

        if (errorCount > 4) {
            bodyMessage = errorMessages.slice(0, 3).join('\n');
            concatenedMessage = headerMessage + '\n\n' + bodyMessage + '\nmore...';
        } else if (errorCount > 1) {
            bodyMessage = errorMessages.join('\n');
            concatenedMessage = headerMessage + '\n\n' + bodyMessage;
        } else if (errorCount === 1) {
            concatenedMessage = errorMessages[0];
        }

        return concatenedMessage;
    }

    private getWorstError(errorList: WideError[], concatenedMessage: string): WideError {
        // set the default error
        let worstError = new WideError(ErrorLevel.Success, 'Unknown');

        try {
            // inline function for condition
            const condition = (level: ErrorLevel): boolean => _.some(errorList, (e: WideError) => e.level === level);
            // inline function to create error    
            const createError = (level: ErrorLevel) =>
                new WideError(level, concatenedMessage);

            // take the worst error
            if (condition(ErrorLevel.Fatal)) {
                worstError = createError(ErrorLevel.Fatal);
                worstError.classed = 'panel-fatal';
            } else if (condition(ErrorLevel.Error)) {
                worstError = createError(ErrorLevel.Error);
                worstError.classed = 'panel-error';
            } else if (condition(ErrorLevel.Warning)) {
                worstError = createError(ErrorLevel.Warning);
                worstError.classed = 'panel-warning';
            } else if (condition(ErrorLevel.Info)) {
                worstError = createError(ErrorLevel.Info);
                worstError.classed = 'panel-info';
            } else if (condition(ErrorLevel.Success)) {
                worstError = createError(ErrorLevel.Success);
            }
        } catch (e) {
            //this.loggerService.logError(e);
        }

        return worstError;
    }
}